/**
 * Phase 39 — "Judged on" at the top of a verdict card: today's drops, or the
 * list's (the gateway's) own stats over a date range — one pill pair and,
 * on a tracker source, the formula's date-range dropdown. The explanation
 * lives behind the ⓘ.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { allowedSourceKinds, DEFAULT_SOURCE_RANGE, DROP_GEN_HOUR_DATE_RANGES, type DropGenVerdictCard, type DropGenVerdictSource, hourRangeKey, parseHourRangeKey } from '@trafficpulse/shared';

import { HintDot, SegmentedPills } from '@/components/ui';
import FormulaGroupDateRangeDropdown from '@/features/flow-optimizer/tabs/calculation/group/date-range/FormulaGroupDateRangeDropdown';

import { SOURCE_HINT, SOURCE_LABELS } from './source-words';

interface Props {
  card: DropGenVerdictCard;
  source: DropGenVerdictSource;
  onChange: (source: DropGenVerdictSource) => void;
}

const rangeFields = (key: string) => {
  const parsed = parseHourRangeKey(key);
  return 'custom' in parsed
    ? {
        dateRange: 'custom',
        dateRangeCustomFrom: parsed.custom.from,
        dateRangeCustomTo: parsed.custom.to,
      }
    : { dateRange: parsed.preset };
};

export const SourceChoice: FC<Props> = ({ card, source, onChange }) => {
  const kinds = allowedSourceKinds(card);
  const tracker = kinds.find((k) => k !== 'drops') ?? 'list';
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="source-choice">
      <SegmentedPills
        label="Judged on"
        options={kinds.map((k) => ({ value: k, label: SOURCE_LABELS[k] }))}
        value={source.kind}
        onChange={(kind) =>
          onChange(
            kind === 'drops'
              ? { kind }
              : {
                  kind: tracker,
                  range: source.kind === 'drops' ? DEFAULT_SOURCE_RANGE : source.range,
                },
          )
        }
      />
      {source.kind !== 'drops' ? (
        <span className="flex items-center gap-1.5" data-testid="source-range">
          over
          <FormulaGroupDateRangeDropdown {...rangeFields(source.range)} presets={DROP_GEN_HOUR_DATE_RANGES} onCommit={(patch) => onChange({ kind: source.kind, range: hourRangeKey(patch) })} />
        </span>
      ) : null}
      <HintDot label={SOURCE_HINT} />
    </div>
  );
};
